/**
 * RC Cola Blog Posts - External CMS loader
 * Fetches RSS/Atom/JSON feeds or parses HTML from a remote blog page.
 */

(function() {
  'use strict';

  if (window.RCColaBlogPostsLoaded) return;
  window.RCColaBlogPostsLoaded = true;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    const sections = document.querySelectorAll('[data-blog-posts-section]');
    if (!sections.length) return;

    sections.forEach(section => {
      initSmartCrop(section);
      if (section.dataset.externalEnabled !== 'true') return;
      loadExternalBlog(section);
    });
  }

  function scheduleSmoothScrollRefresh(reason) {
    if (typeof window.refreshSmoothScroll === 'function') {
      window.refreshSmoothScroll(reason);
    }
  }

  function refreshAfterImages(container, reason) {
    if (!container) return;
    const images = Array.from(container.querySelectorAll('img'));
    if (!images.length) return;

    let pending = 0;
    const onDone = () => {
      pending -= 1;
      if (pending <= 0) scheduleSmoothScrollRefresh(reason);
    };

    images.forEach(img => {
      if (img.complete) return;
      pending += 1;
      img.addEventListener('load', onDone, { once: true });
      img.addEventListener('error', onDone, { once: true });
    });

    if (pending === 0) {
      scheduleSmoothScrollRefresh(reason);
    }
  }

  async function loadExternalBlog(section) {
    const list = section.querySelector('[data-blog-posts-list]');
    const status = section.querySelector('[data-blog-posts-status]');

    const feedUrl = (section.dataset.externalFeedUrl || '').trim();
    const pageUrl = (section.dataset.externalUrl || '').trim();
    const targetUrl = feedUrl || pageUrl;

    if (!targetUrl) {
      setStatus(status, 'Set the external blog URL in the section settings.');
      return;
    }

    const proxyTemplate = (section.dataset.corsProxy || '').trim();
    const requestUrl = buildRequestUrl(targetUrl, proxyTemplate);
    const openNewTab = section.dataset.openNewTab === 'true';
    const showDate = section.dataset.showDate === 'true';
    const showAuthor = section.dataset.showAuthor === 'true';
    const defaultAuthor = (section.dataset.defaultAuthor || '').trim();
    const imageSelector = (section.dataset.imageSelector || '').trim();
    const preferOgImage = section.dataset.preferOgImage === 'true';
    const limit = toNumber(section.dataset.articlesCount, 3);
    const excerptLength = toNumber(section.dataset.excerptLength, 50);
    const baseUrl = pageUrl || targetUrl;
    const originBlocked = !proxyTemplate && !isSameOrigin(targetUrl);

    list.setAttribute('aria-busy', 'true');

    let responseText = '';
    try {
      const res = await fetch(requestUrl, { credentials: 'omit' });
      if (!res.ok) {
        throw new Error(`Request failed: ${res.status}`);
      }
      responseText = await res.text();
    } catch (err) {
      console.error('Blog Posts: Failed to load external feed.', err);
      if (originBlocked) {
        setStatus(status, 'Unable to load posts. The external site blocks browser requests. Add a CORS proxy in the section settings.');
      } else {
        setStatus(status, 'Unable to load posts. Check the URL or CORS proxy.');
      }
      list.removeAttribute('aria-busy');
      return;
    }

    const posts = parseFeed(responseText, baseUrl, imageSelector);

    if (!posts.length) {
      setStatus(status, 'No posts found.');
      list.removeAttribute('aria-busy');
      return;
    }

    const slicedPosts = posts.slice(0, limit);

    if (preferOgImage) {
      await hydrateOgImages(slicedPosts, {
        proxyTemplate: proxyTemplate,
        baseUrl: baseUrl,
        originBlocked: originBlocked
      });
    }

    list.innerHTML = '';
    slicedPosts.forEach(post => {
      list.appendChild(buildCard(post, {
        excerptLength: excerptLength,
        showDate: showDate,
        showAuthor: showAuthor,
        defaultAuthor: defaultAuthor,
        openNewTab: openNewTab
      }));
    });

    list.removeAttribute('aria-busy');
    if (status) status.remove();
    initSmartCrop(section);
    scheduleSmoothScrollRefresh('blog-posts:render');
    refreshAfterImages(list, 'blog-posts:images');
  }

  function buildRequestUrl(targetUrl, proxyTemplate) {
    if (!proxyTemplate) return targetUrl;
    if (proxyTemplate.includes('{url}')) {
      return proxyTemplate.replace('{url}', encodeURIComponent(targetUrl));
    }
    return proxyTemplate + targetUrl;
  }

  function parseFeed(text, baseUrl, imageSelector) {
    const trimmed = (text || '').trim();
    if (!trimmed) return [];

    if (trimmed[0] === '{' || trimmed[0] === '[') {
      try {
        return parseJsonFeed(JSON.parse(trimmed), baseUrl, imageSelector);
      } catch (err) {
        console.warn('Blog Posts: JSON parse error.', err);
        return [];
      }
    }

    if (trimmed[0] === '<') {
      const xmlDoc = new DOMParser().parseFromString(trimmed, 'text/xml');
      if (!xmlDoc.querySelector('parsererror')) {
        if (xmlDoc.querySelector('rss') || xmlDoc.querySelector('channel')) {
          return parseRss(xmlDoc, baseUrl);
        }
        if (xmlDoc.querySelector('feed')) {
          return parseAtom(xmlDoc, baseUrl);
        }
      }
      return parseHtml(trimmed, baseUrl, imageSelector);
    }

    return [];
  }

  function parseJsonFeed(data, baseUrl, imageSelector) {
    if (!data) return [];

    if (data.contents && typeof data.contents === 'string') {
      return parseFeed(data.contents, baseUrl, imageSelector);
    }

    if (data.content && typeof data.content === 'string') {
      return parseFeed(data.content, baseUrl, imageSelector);
    }

    if (Array.isArray(data)) {
      return data.map(item => normalizeJsonItem(item, baseUrl)).filter(Boolean);
    }

    if (Array.isArray(data.items)) {
      return data.items.map(item => normalizeJsonItem(item, baseUrl)).filter(Boolean);
    }

    if (Array.isArray(data.objects)) {
      return data.objects.map(item => normalizeJsonItem(item, baseUrl)).filter(Boolean);
    }

    if (Array.isArray(data.results)) {
      return data.results.map(item => normalizeJsonItem(item, baseUrl)).filter(Boolean);
    }

    if (Array.isArray(data.posts)) {
      return data.posts.map(item => normalizeJsonItem(item, baseUrl)).filter(Boolean);
    }

    return [];
  }

  function normalizeJsonItem(item, baseUrl) {
    if (!item) return null;

    const title = item.title && item.title.rendered ? item.title.rendered : item.title;
    const url = resolveUrl(item.link || item.url || item.absolute_url || item.external_url, baseUrl);
    const date = item.date || item.published_at || item.date_published || item.updated;
    const author = (item.author && item.author.name) || item.author_name || item.author;
    const excerpt = item.excerpt && item.excerpt.rendered ? item.excerpt.rendered : (item.summary || item.description || item.post_summary);

    let image = item.featured_image || item.image || null;
    if (image && typeof image === 'object') {
      const bestFromSizes = pickBestFromSizes(image.media_details && image.media_details.sizes);
      image = image.url || image.src || image.source_url || image.guid || bestFromSizes || '';
    }
    if (!image && item._embedded && item._embedded['wp:featuredmedia'] && item._embedded['wp:featuredmedia'][0]) {
      const media = item._embedded['wp:featuredmedia'][0];
      image = media.source_url || pickBestFromSizes(media.media_details && media.media_details.sizes);
    }

    return normalizePost({
      title: stripHtml(title),
      url: url,
      date: date,
      author: author,
      excerpt: stripHtml(excerpt),
      image: resolveUrl(image, baseUrl)
    });
  }

  function parseRss(xmlDoc, baseUrl) {
    const items = Array.from(xmlDoc.querySelectorAll('item'));
    return items.map(item => {
      const title = textContent(item, 'title');
      const url = resolveUrl(textContent(item, 'link'), baseUrl);
      const date = textContent(item, 'pubDate') || textContent(item, 'dc\\:date');
      const author = textContent(item, 'dc\\:creator') || textContent(item, 'author');
      const description = textContent(item, 'description') || textContent(item, 'content\\:encoded');
      const image =
        resolveUrl(attrValue(item, 'media\\:content', 'url'), baseUrl) ||
        extractImageFromHtml(description, baseUrl);

      return normalizePost({
        title: stripHtml(title),
        url: url,
        date: date,
        author: author,
        excerpt: stripHtml(description),
        image: image
      });
    }).filter(Boolean);
  }

  function parseAtom(xmlDoc, baseUrl) {
    const entries = Array.from(xmlDoc.querySelectorAll('entry'));
    return entries.map(entry => {
      const title = textContent(entry, 'title');
      const linkEl = entry.querySelector('link[rel="alternate"]') || entry.querySelector('link');
      const url = resolveUrl(linkEl ? linkEl.getAttribute('href') : '', baseUrl);
      const date = textContent(entry, 'updated') || textContent(entry, 'published');
      const author = textContent(entry, 'author > name');
      const summary = textContent(entry, 'summary') || textContent(entry, 'content');

      return normalizePost({
        title: stripHtml(title),
        url: url,
        date: date,
        author: author,
        excerpt: stripHtml(summary),
        image: extractImageFromHtml(summary, baseUrl)
      });
    }).filter(Boolean);
  }

  function parseHtml(html, baseUrl, imageSelector) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const articleEls = Array.from(doc.querySelectorAll('article'));
    const posts = articleEls.map(article => normalizePost({
      title: stripHtml(textFromNode(article.querySelector('h1, h2, h3'))),
      url: resolveUrl(hrefFromNode(article.querySelector('a[href]')), baseUrl),
      date: textFromNode(article.querySelector('time')) || article.querySelector('time')?.getAttribute('datetime') || '',
      author: textFromNode(article.querySelector('[class*="author"], .author')),
      excerpt: stripHtml(textFromNode(article.querySelector('p'))),
      image: resolveUrl(srcFromNode(article.querySelector(imageSelector || 'img')), baseUrl)
    })).filter(Boolean);

    if (posts.length) return posts;

    const links = Array.from(doc.querySelectorAll('h2 a, h3 a'))
      .filter(anchor => anchor.href && anchor.textContent.trim());

    return links.map(anchor => normalizePost({
      title: stripHtml(anchor.textContent),
      url: resolveUrl(anchor.getAttribute('href') || anchor.href, baseUrl),
      excerpt: '',
      image: null
    })).filter(Boolean);
  }

  function buildCard(post, options) {
    const card = document.createElement('article');
    card.className = 'blog-card';

    const link = document.createElement('a');
    link.className = 'blog-card__link';
    link.href = post.url || '#';
    link.setAttribute('aria-label', `Read article: ${post.title || 'Blog post'}`);
    if (options.openNewTab) {
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
    }

    const imageWrapper = document.createElement('div');
    imageWrapper.className = 'blog-card__image-wrapper';
    imageWrapper.dataset.hasImage = post.image ? 'true' : 'false';

    const image = document.createElement('img');
    image.className = 'blog-card__image';
    image.src = post.image || 'https://placehold.co/500x485/0040B6/white?text=No+Image';
    image.alt = post.title || 'Blog post image';
    image.loading = 'lazy';
    image.width = 500;
    image.height = 485;

    imageWrapper.style.setProperty('--blog-card-image', `url("${escapeCssUrl(image.src)}")`);
    imageWrapper.appendChild(image);
    attachSmartCrop(imageWrapper, image);

    const content = document.createElement('div');
    content.className = 'blog-card__content';

    const title = document.createElement('h3');
    title.className = 'blog-card__title';
    title.textContent = post.title || 'Untitled post';

    const excerpt = document.createElement('p');
    excerpt.className = 'blog-card__excerpt';
    excerpt.textContent = truncateWords(post.excerpt || '', options.excerptLength);

    const readMore = document.createElement('span');
    readMore.className = 'blog-card__read-more';
    readMore.textContent = 'read more >';

    content.appendChild(title);
    content.appendChild(excerpt);
    content.appendChild(readMore);

    const meta = document.createElement('div');
    meta.className = 'blog-card__meta';

    const authorName = options.showAuthor ? (post.author || options.defaultAuthor || '') : '';
    const hasDate = options.showDate && post.date;
    const hasAuthor = options.showAuthor && authorName;

    if (hasDate) {
      const timeEl = document.createElement('time');
      timeEl.className = 'blog-card__date';
      timeEl.dateTime = formatDateISO(post.date);
      timeEl.textContent = formatDateDisplay(post.date);
      meta.appendChild(timeEl);
    }

    if (hasDate && hasAuthor) {
      const separator = document.createElement('span');
      separator.className = 'blog-card__separator';
      separator.textContent = '|';
      meta.appendChild(separator);
    }

    if (hasAuthor) {
      const author = document.createElement('span');
      author.className = 'blog-card__author';
      author.textContent = authorName;
      meta.appendChild(author);
    }

    if (hasDate || hasAuthor) {
      content.appendChild(meta);
    }

    link.appendChild(imageWrapper);
    link.appendChild(content);
    card.appendChild(link);

    return card;
  }

  function normalizePost(post) {
    if (!post || !post.url || !post.title) return null;

    return {
      title: post.title,
      url: post.url,
      date: post.date || '',
      author: post.author || '',
      excerpt: post.excerpt || '',
      image: post.image || ''
    };
  }

  function initSmartCrop(section) {
    if (!section) return;
    const wrappers = Array.from(section.querySelectorAll('.blog-card__image-wrapper'));
    wrappers.forEach(wrapper => {
      const image = wrapper.querySelector('img');
      if (image) attachSmartCrop(wrapper, image);
    });
  }

  function attachSmartCrop(wrapper, image) {
    if (!wrapper || !image) return;

    const updateZoom = () => {
      const naturalWidth = image.naturalWidth || image.width || 0;
      const naturalHeight = image.naturalHeight || image.height || 0;
      if (!naturalWidth || !naturalHeight) return;

      const containerWidth = wrapper.clientWidth || 1;
      const containerHeight = wrapper.clientHeight || 1;
      const containerRatio = containerWidth / containerHeight;
      const imageRatio = naturalWidth / naturalHeight;
      const ratioDelta = imageRatio / containerRatio;

      let zoom = 1;
      if (ratioDelta >= 2.2) {
        zoom = 1.7;
      } else if (ratioDelta >= 1.9) {
        zoom = 1.55;
      } else if (ratioDelta >= 1.6) {
        zoom = 1.4;
      } else if (ratioDelta >= 1.3) {
        zoom = 1.25;
      }

      wrapper.style.setProperty('--blog-card-scale', '1');
      wrapper.style.setProperty('--blog-card-scale-hover', String(zoom));
    };

    if (image.complete) {
      updateZoom();
    } else {
      image.addEventListener('load', updateZoom, { once: true });
      image.addEventListener('error', () => wrapper.style.setProperty('--blog-card-zoom', '1'), { once: true });
    }
  }

  function escapeCssUrl(url) {
    if (!url) return '';
    return String(url).replace(/"/g, '%22').replace(/\\/g, '%5C');
  }

  function textContent(root, selector) {
    const node = root.querySelector(selector);
    return node ? node.textContent.trim() : '';
  }

  function attrValue(root, selector, attr) {
    const node = root.querySelector(selector);
    return node ? node.getAttribute(attr) : '';
  }

  function textFromNode(node) {
    return node ? node.textContent.trim() : '';
  }

  function hrefFromNode(node) {
    return node ? node.getAttribute('href') : '';
  }

  function srcFromNode(node) {
    return node ? bestImageFromElement(node) : '';
  }

  function stripHtml(value) {
    if (!value) return '';
    const doc = new DOMParser().parseFromString(String(value), 'text/html');
    return (doc.body ? doc.body.textContent : doc.textContent || '').trim();
  }

  function extractImageFromHtml(html, baseUrl) {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(String(html), 'text/html');
    const img = doc.querySelector('img');
    return img ? resolveUrl(bestImageFromElement(img), baseUrl) : '';
  }

  function truncateWords(text, maxWords) {
    if (!text) return '';
    const words = text.split(/\s+/).filter(Boolean);
    if (words.length <= maxWords) return words.join(' ');
    return `${words.slice(0, maxWords).join(' ')}...`;
  }

  function toNumber(value, fallback) {
    const number = Number(value);
    return Number.isFinite(number) && number > 0 ? number : fallback;
  }

  function formatDateISO(dateValue) {
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return '';
    return date.toISOString().slice(0, 10);
  }

  function formatDateDisplay(dateValue) {
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}.${month}.${year}`;
  }

  function setStatus(node, message) {
    if (!node) return;
    node.textContent = message;
  }

  function isSameOrigin(url) {
    try {
      const target = new URL(url, window.location.href);
      return target.origin === window.location.origin;
    } catch (err) {
      return false;
    }
  }

  function resolveUrl(value, baseUrl) {
    if (!value) return '';
    if (!baseUrl) return value;
    try {
      return new URL(value, baseUrl).toString();
    } catch (err) {
      return value;
    }
  }

  function bestImageFromElement(node) {
    if (!node) return '';
    const candidates = [
      node.getAttribute('data-src'),
      node.getAttribute('data-lazy-src'),
      node.getAttribute('data-original'),
      node.getAttribute('data-srcset'),
      node.getAttribute('srcset'),
      node.getAttribute('src')
    ].filter(Boolean);

    for (const candidate of candidates) {
      if (candidate.includes(',') || /\s\d+(w|x)\b/.test(candidate)) {
        const best = pickBestFromSrcset(candidate);
        if (best) return best;
        continue;
      }
      if (candidate) return candidate;
    }

    return '';
  }

  function pickBestFromSrcset(srcset) {
    const entries = String(srcset)
      .split(',')
      .map(entry => entry.trim())
      .filter(Boolean)
      .map(entry => {
        const parts = entry.split(/\s+/);
        return {
          url: parts[0],
          descriptor: parts[1] || ''
        };
      });

    if (!entries.length) return '';

    entries.sort((a, b) => srcsetScore(b.descriptor) - srcsetScore(a.descriptor));
    return entries[0].url;
  }

  function srcsetScore(descriptor) {
    if (!descriptor) return 0;
    const match = descriptor.match(/(\d+)(w|x)/);
    if (!match) return 0;
    const value = Number(match[1]);
    if (!Number.isFinite(value)) return 0;
    return match[2] === 'x' ? value * 10000 : value;
  }

  function pickBestFromSizes(sizes) {
    if (!sizes || typeof sizes !== 'object') return '';
    const entries = Object.values(sizes)
      .map(size => ({
        url: size && (size.source_url || size.url || size.src),
        width: Number(size && size.width)
      }))
      .filter(entry => entry.url && Number.isFinite(entry.width));

    if (!entries.length) return '';
    entries.sort((a, b) => b.width - a.width);
    return entries[0].url;
  }

  async function hydrateOgImages(posts, options) {
    if (!posts.length) return;
    if (options.originBlocked && !options.proxyTemplate) {
      console.warn('Blog Posts: og:image fetch skipped. Add a CORS proxy to enable.');
      return;
    }

    const tasks = posts.map(async post => {
      if (!post || !post.url) return;
      const requestUrl = buildRequestUrl(post.url, options.proxyTemplate);
      try {
        const res = await fetch(requestUrl, { credentials: 'omit' });
        if (!res.ok) return;
        const html = await res.text();
        const ogImage = extractOgImage(html, post.url);
        if (ogImage) {
          post.image = ogImage;
        }
      } catch (err) {
        console.warn('Blog Posts: og:image fetch failed.', err);
      }
    });

    await Promise.all(tasks);
  }

  function extractOgImage(html, baseUrl) {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(String(html), 'text/html');
    const selectors = [
      'meta[property=\"og:image\"]',
      'meta[property=\"og:image:url\"]',
      'meta[name=\"twitter:image\"]',
      'meta[name=\"twitter:image:src\"]',
      'meta[name=\"image\"]'
    ];

    for (const selector of selectors) {
      const tag = doc.querySelector(selector);
      const content = tag ? tag.getAttribute('content') : '';
      if (content) return resolveUrl(content, baseUrl);
    }

    return '';
  }
})();
